import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import CardMedia from "@mui/material/CardMedia";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import Button from '@directv-digital/fusion.atoms.button';

const bps = DtvTheme.breakpoints;

export interface MyFreeInfoBlockProps {
  /** Root id prefix — the subheading id is derived as `${idPrefix}-myfree-subheading`.
   * Omit to render it without an id. Does NOT affect the banner id — see `bannerId`. */
  idPrefix?: string;
  /** [WAT-11256 round-6 S14] Banner container id. Defaults to `myfreeDtv-info-
   * container` — the stable id the browse-view accordion has always rendered, which
   * this extraction must not silently change: this container holds the Sign Up CTA,
   * a conversion element, and off-repo GTM click triggers / QA or e2e locators may
   * key on it. The search view opts into its own id explicitly at its call site. */
  bannerId?: string;
  /** CMS-authored MyFree config. Fields this block reads: `subheading.content`
   * (satellite caveat text), `logo` (banner image src), and
   * `packageData.gpCtaProps.{label,targeturl}` (the Sign Up CTA — both fall back to
   * `"Sign Up"` / `"#"` when authored but empty, and the CTA is omitted entirely
   * when `gpCtaProps` itself isn't authored). */
  myFreeConfig?: any;
  /** Satellite-only: the FAST channels bucketed into MyFree aren't receivable on
   * satellite hardware, a caveat that doesn't apply to the stream lineup. */
  isSatelliteChlup?: boolean;
  subheadingTextColor?: string;
  bannerBackground?: string;
  /** [WAT-11717] Space above the logo/CTA banner. Defaults to the original
   * `DtvTheme.spacing(3)` (12px) the browse-view accordion (CategoryHeader.tsx) has
   * always used, where it's invisible against that view's single continuous
   * AccordionSummary background. The search-view caller (PackageCmprGrid.tsx) passes
   * `DtvTheme.spacing(0)`: there, the header label and this banner are two
   * separately-backgrounded boxes, so any margin between them shows as a bare gap
   * rather than breathing room. */
  bannerMarginTop?: string;
  // [WAT-11774] The SUBHEADING box's own top margin. `bannerMarginTop` zeroed the banner's
  // gap for the search view but this box's margin was hardcoded, so on satellite CHLUP with
  // an authored subheading AND a MyFree match in the search results, the bare strip the
  // banner override existed to remove was still rendered by the box above it — the gap was
  // only half closed.
  subheadingMarginTop?: string;
}

/**
 * @description [WAT-11256 S9] The MyFree DIRECTV satellite subheading + logo/CTA
 * banner, shared between the browse-view accordion (CategoryHeader.tsx) and the
 * search-view MyFree block (PackageCmprGrid.tsx) — previously two near-identical
 * copies (same structure, same markup) that had already drifted from each other:
 * PackageCmprGrid's copy had picked up a stray horizontal `margin` on the subheading
 * (see the inline comment below) that CategoryHeader's original never had, plus a
 * different `onError` target. This extraction is what makes them identical again.
 *
 * Deliberately does NOT include the "MyFree DIRECTV" label itself: that's each
 * caller's own heading typography (CategoryHeader's shared category `Typography`,
 * PackageCmprGrid's own `DtvText`), not a reusable part of this block.
 */
const MyFreeInfoBlock = ({
  idPrefix,
  bannerId = "myfreeDtv-info-container",
  myFreeConfig,
  isSatelliteChlup,
  subheadingTextColor,
  bannerBackground,
  bannerMarginTop = DtvTheme.spacing(3),
  subheadingMarginTop = DtvTheme.spacing(3)
}: MyFreeInfoBlockProps) => {
  const myFreeDTVCta = myFreeConfig?.packageData?.gpCtaProps;
  return (
    <>
      {isSatelliteChlup && myFreeConfig?.subheading?.content && (
        <DtvBox
          id={idPrefix ? `${idPrefix}-myfree-subheading` : undefined}
          sx={{
            width: "100%",
            backgroundColor: colorPalette.neutral[100],
            // [WAT-11256 S10] Padding-only inset, no horizontal margin — a sibling
            // `width: "100%"` box PLUS a horizontal margin adds up to more than 100%
            // of the parent's width (36px total here), overflowing the grid
            // horizontally. The original CategoryHeader version never had this
            // margin; only the port to PackageCmprGrid's search block introduced it.
            padding: DtvTheme.spacing(2),
            marginTop: subheadingMarginTop
          }}
        >
          <DtvText
            content={myFreeConfig?.subheading?.content}
            variant="body3"
            sx={{ color: subheadingTextColor }}
          />
        </DtvBox>
      )}
      <DtvBox
        id={bannerId}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bannerBackground,
          marginTop: bannerMarginTop,
          gap: "32px"
        }}
      >
        <CardMedia
          alt="MyFreeDTV"
          component="img"
          loading="lazy"
          src={myFreeConfig?.logo}
          sx={{
            height: "27px",
            width: "97px",
            [bps.down(bps.values.sm)]: {
              height: "25px",
              width: "87px"
            }
          }}
          // [WAT-11256 nit] Hides the image itself, not its parent — the parent is
          // this flex row, which also holds the Sign Up CTA. Hiding the whole row on
          // a 404'd asset silently removes a conversion element along with a broken
          // image, when only the image is actually broken.
          // [WAT-11256 round-6 S13] Logs before hiding (matching ChannelRow.tsx's
          // format so they're greppable together) — the MyFree logo host is time- and
          // region-variable, so a silent hide is indistinguishable from "the CMS
          // never authored a logo" and a live failure here previously went unpaged.
          // Also drops the whole row (not just the image) when there's no CTA either,
          // so a 404'd logo with no authored CTA doesn't leave an empty, still-
          // painted banner box behind.
          onError={e => {
            const img = e?.currentTarget;
            if (!img) {
              return;
            }
            console.error("[PackagesGrid][MyFreeInfoBlock] MyFree logo failed to load", myFreeConfig?.logo);
            img.style.display = "none";
            if (!myFreeDTVCta && img.parentElement) {
              img.parentElement.style.display = "none";
            }
          }}
        />
        {myFreeDTVCta && (
          <Button
            buttontype="secondary"
            href={myFreeDTVCta?.targeturl || "#"}
            size="small"
          >
            {myFreeDTVCta?.label || "Sign Up"}
          </Button>
        )}
      </DtvBox>
    </>
  );
};

export const MemoizedMyFreeInfoBlock = React.memo(MyFreeInfoBlock);
